import axios from 'axios';
import { fetchBlobUrl } from '../components/subtitles/ReadBlobToString';
import { uploadToBlobStorage } from '../folders/subtitles/BlobVttFile';
import { getAuthHeaders } from '../utils/authUtils';

class TextFileService {
    /**
     * Create a new text file
     */
    async createTextFile(path, name, content, sourceLanguage, targetLanguages, autoTranslate = false) {
        const headers = getAuthHeaders(true);

        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/textfiles`,
            {
                path: path,
                name: name,
                content: content,
                sourceLanguage: sourceLanguage,
                targetLanguages: targetLanguages.map(lang => lang.label).join(','),
                autoTranslate: String(autoTranslate),
            },
            { headers },
        );

        return response.data;
    }

    /**
     * Create multiple text files in bulk
     */
    async createTextFilesBulk(path, files, sourceLanguage, targetLanguages, autoTranslate = false, onProgress) {
        const headers = getAuthHeaders(true);
        const fileRequests = [];

        // First, read all file contents
        for (let i = 0; i < files.length; i++) {
            try {
                const content = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(e.target.result);
                    reader.onerror = reject;
                    reader.readAsText(files[i]);
                });

                fileRequests.push({
                    path: path,
                    name: files[i].name,
                    content: content,
                    sourceLanguage: sourceLanguage,
                    targetLanguages: targetLanguages.map(lang => lang.label).join(','),
                    autoTranslate: String(autoTranslate),
                });

                // Update progress
                if (onProgress) {
                    onProgress(i, ((i + 1) / files.length) * 50);
                }
            } catch (error) {
                console.error(`Error reading file ${files[i].name}:`, error);
            }
        }

        // Send all files to bulk endpoint
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/v2/textfiles/bulk`,
                fileRequests,
                { headers },
            );

            // Map over responses and update progress
            const results = response.data.map((result, index) => {
                if (onProgress) {
                    onProgress(index, 50 + ((index + 1) / files.length) * 50);
                }
                return result;
            });

            return results;
        } catch (error) {
            console.error('Error during bulk upload:', error);
            throw error;
        }
    }

    /**
     * Get a text file by ID
     */
    async getTextFileById(id) {
        const headers = getAuthHeaders(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v2/textfiles/${id}`, { headers });

        return response.data;
    }

    /**
     * Get the content of a text file
     */
    async getTextFileContent(url) {
        try {
            const textData = await fetchBlobUrl(url);
            return textData;
        } catch (error) {
            console.error('Error fetching text file:', error);
            return null;
        }
    }

    /**
     * Update text file content
     */
    async updateTextFileContent(id, languageCode, content, url, saveType = 'MANUAL', highlights = []) {
        const headers = getAuthHeaders(true);

        if (!url) {
            throw new Error('URL is required to update text file content.');
        }

        await uploadToBlobStorage(url, content, 'text');

        const response = await axios.put(
            `${process.env.REACT_APP_API_URL}/api/v2/textfiles/${id}/content`,
            { languageCode, saveType, highlights },
            {
                headers,
            },
        );

        return response.data;
    }

    /**
     * Get translation content
     */
    async getTranslationContent(id, languageCode) {
        const headers = getAuthHeaders(true);

        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/v2/textfiles/${id}/translations/${languageCode}/content`,
            {
                headers,
            },
        );

        return response.data.content;
    }

    /**
     * Start off translation from the source file to target languages, given an id.
     * This will initiate the translation process on the server side.
     * @param {string} id - The ID of the text file to translate.
     * @param {string} sourceLanguage - The source language code.
     * @param {Array<string>} targetLanguages - The target language codes.
     */
    async startTranslation(id, sourceLanguage, targetLanguages) {
        const headers = getAuthHeaders(true);

        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/textfiles/${id}/translations/start`,
            {
                sourceLanguage,
                targetLanguages,
            },
            { headers },
        );

        return response.data;
    }

    // get translation status
    /**
     * Get Translation Status
     * @param {string} id - The ID of the text file.
     * @param {string} languageCode - The language code for the translation.
     * @returns {Promise<string>} - The status of the translation.
     */
    async getTranslationStatus(id, languageCode) {
        const headers = getAuthHeaders(true);

        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/v2/textfiles/${id}/translations/${languageCode}/status`,
            { headers },
        );

        return response.data.status;
    }

    /**
     * Add or update a translation
     */
    async addTranslation(id, languageCode, translatedText) {
        const headers = getAuthHeaders(true);

        const response = await axios.put(
            `${process.env.REACT_APP_API_URL}/api/v2/textfiles/${id}/translations/${languageCode}`,
            { translatedText },
            { headers },
        );

        return response.data;
    }

    /**
     * Delete a text file
     */
    async delete(id) {
        const headers = getAuthHeaders(true);

        const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/v2/textfiles/${id}`, { headers });

        return response.data;
    }

    /**
     * Rename a text file
     */
    async rename(id, currentPath, newName) {
        const headers = getAuthHeaders(true);

        const response = await axios.put(
            `${process.env.REACT_APP_API_URL}/api/v2/textfiles/${id}/rename`,
            { currentPath, newName },
            { headers },
        );

        return response.data;
    }

    async getTextFileLanguageHistory(id, languageCode) {
        const headers = getAuthHeaders(true);
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/v2/textfiles/${id}/history/${languageCode}`,
            { headers },
        );
        return response.data;
    }

    async restoreTextFileLanguageVersion(id, languageCode, versionNumber) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/textfiles/${id}/history/${languageCode}/restore`,
            { versionNumber },
            { headers },
        );
        return response.data;
    }

    async updateTextFileTranslationStage(id, languageCode, processStage) {
        const headers = getAuthHeaders(true);
        const response = await axios.put(
            `${process.env.REACT_APP_API_URL}/api/v2/textfiles/${id}/translations/${languageCode}/stage`,
            { processStage },
            { headers },
        );
        return response.data;
    }

    /**
     * Generate glossary for a text file
     */
    async generateGlossary(id, sourceLanguage, targetLanguage) {
        const headers = getAuthHeaders(true);
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v2/textfiles/${id}/glossary/generate`,
            {
                sourceLanguage,
                targetLanguage,
            },
            { headers },
        );
        return response.data;
    }
}

const textFileServiceInstance = new TextFileService();
export default textFileServiceInstance;
